import { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const ADSENSE_SCRIPT_SRC =
  'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-9243961427413079';

/**
 * Keeps AdSense in step with sign-in state.
 *
 * It never performs the *initial* load. public/index.html owns that: since
 * 2026-10-01 it starts adsbygoogle.js on the visitor's first real interaction
 * (scroll, wheel, tap or key), guarded by the same localStorage token this
 * component's `isAuthenticated` derives from, and records that in
 * `window.__sdAds.started`. Injecting the tag here before the gate fires would undo
 * the gate for every page view, which is what made bot traffic request ads.
 *
 * What it still does: remove the tag when a reader signs in mid-session, and add it
 * back when one signs out (by then they have interacted, so the gate has fired).
 */
const AdSenseLoader: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  useEffect(() => {
    if (loading) return;
    if (typeof document === 'undefined') return;

    const existing = document.querySelector(
      'script[src*="pagead2.googlesyndication.com"]'
    );

    if (isAuthenticated) {
      if (existing) existing.remove();
      return;
    }

    if (existing) return;

    // The gate in index.html decides the first load: wait until a real interaction has
    // fired it, and never load for an automated browser (which it marks `blocked`).
    const gate = (window as unknown as { __sdAds?: { started: boolean; blocked?: boolean } }).__sdAds;
    if (gate && (gate.blocked || !gate.started)) return;
    if (navigator.webdriver) return;

    // index.html pauses ad requests until a real interaction; a reader signing out has
    // interacted, so lift the pause before (re)loading the script.
    const ads = window as unknown as { adsbygoogle?: unknown[] & { pauseAdRequests?: number } };
    (ads.adsbygoogle = ads.adsbygoogle || []).pauseAdRequests = 0;

    const script = document.createElement('script');
    script.src = ADSENSE_SCRIPT_SRC;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.setAttribute('data-cfasync', 'false');
    document.head.appendChild(script);
  }, [isAuthenticated, loading]);

  return null;
};

export default AdSenseLoader;
